import { MaterialCommunityIcons } from '@expo/vector-icons';
import { Severity } from '@gripp/shared-logic';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { Colors } from '../../themes/colors';
import { IssuesSeverityIcon } from '../shared/IssuesSeverityIcons';

type PinnedActivity = {
  id: string;
  title: string;
  severity: Severity;
};

type PinnedActivitiesProps = {
  assetId: string;
  pinnedActivities: PinnedActivity[];
  showAll?: boolean;
  onViewAllPress?: () => void;
  onActivityPress?: (activityId: string) => void;
};

export const PinnedActivitiesOld = ({
  assetId,
  pinnedActivities,
  showAll = false,
  onViewAllPress,
  onActivityPress,
}: PinnedActivitiesProps) => {
  const { t } = useTranslation();

  const handleActivityPress = (activityId: string) => {
    if (onActivityPress) {
      onActivityPress(activityId);
    }
  };

  const handleViewAllPress = () => {
    if (onViewAllPress) {
      onViewAllPress();
    }
  };

  if (!pinnedActivities || pinnedActivities.length === 0) {
    return null;
  }

  // Show only 3 activities by default, or all if showAll is true
  const displayedActivities = showAll
    ? pinnedActivities
    : pinnedActivities.slice(0, 3);

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>{t('asset.pinnedActivity')}</Text>
        {!showAll && pinnedActivities.length > 3 && (
          <Pressable onPress={handleViewAllPress}>
            <Text style={styles.viewAllText}>{t('asset.viewAll')}</Text>
          </Pressable>
        )}
      </View>

      <View style={styles.activitiesList}>
        {displayedActivities.map((activity) => (
          <Pressable
            key={activity.id}
            style={styles.activityItem}
            onPress={() => handleActivityPress(activity.id)}
          >
            <IssuesSeverityIcon severity={activity.severity} />
            <Text style={styles.activityText} numberOfLines={1}>
              {activity.title}
            </Text>
            <View style={styles.chevronContainer}>
              <MaterialCommunityIcons
                name="chevron-right"
                size={20}
                color={Colors.grayText}
              />
            </View>
          </Pressable>
        ))}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: Colors.white,
    padding: 16,
    borderRadius: 12,
    marginBottom: 16,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  title: {
    fontSize: 16,
    fontWeight: '700',
    color: Colors.black,
  },
  viewAllText: {
    fontSize: 16,
    fontWeight: '500',
    color: Colors.primary,
  },
  activitiesList: {
    flexDirection: 'column',
    gap: 12,
  },
  activityItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: Colors.white,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: Colors.secondaryGrayHeader,
    shadowColor: Colors.black,
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  iconContainer: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
    flexShrink: 0,
  },
  activityText: {
    flex: 1,
    fontSize: 16,
    fontWeight: '500',
    color: Colors.black,
    marginRight: 8,
  },
  chevronContainer: {
    flexShrink: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
});
